import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Box,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Search,
  RemoveCircleOutline,
  CheckCircle,
} from "@mui/icons-material";

const FriendSelectionDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  queryUsers,
  initialSelection,
  type,
  excludeIds = [], // IMPORTANTE: Lista de IDs a no mostrar (incluyendo al usuario actual)
}) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);

  // Sincronización de estado inicial
  useEffect(() => {
    if (open) {
      setSelected(initialSelection || []);
      setSearch("");
      setResults([]);
    }
  }, [open, initialSelection]);

  // Lógica de búsqueda con Debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (search.trim()) {
        queryUsers(search, (data) => {
          // Filtramos los resultados para que NO aparezcan los excluidos (tú mismo u otros ya seleccionados)
          const filteredResults = data.filter(
            (u) => !excludeIds.includes(u.id)
          );
          setResults(filteredResults);
        });
      } else {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [search, queryUsers, excludeIds]);

  const toggleFriend = (friend) => {
    const isSelected = selected.some((f) => f.id === friend.id);
    if (isSelected) {
      setSelected(selected.filter((f) => f.id !== friend.id));
    } else {
      setSelected([...selected, friend]);
    }
  };

  // Combinar resultados con seleccionados para que no desaparezcan al borrar búsqueda
  // Pero filtrando siempre los IDs excluidos por seguridad
  const displayList = [...results];
  selected.forEach((s) => {
    if (!displayList.some((d) => d.id === s.id) && !excludeIds.includes(s.id)) {
      displayList.push(s);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 99999 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: "80vh",
          maxHeight: "600px",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* CABECERA (Fija) */}
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e4e6eb",
          display: "flex",
          alignItems: "center",
          p: 1.5,
        }}
      >
        <IconButton onClick={onClose}>
          <ArrowBack />
        </IconButton>
        <Typography
        sx={{
          borderRadius: 3,
          height: { xs: "80vh", md: "auto" },
          maxHeight: { xs: "600px", md: "calc(100vh - 120px)" },
          display: "flex",
          flexDirection: "column",
        }}
        >
          {title}
        </Typography>
        <Box sx={{ width: 40 }} />
      </DialogTitle>

      {/* CUERPO */}
      <DialogContent
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: "hidden",
        }}
      >
        <TextField
          autoFocus
          fullWidth
          placeholder="Busca un amigo..."
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: "text.secondary", mr: 1 }} />,
            sx: {
              borderRadius: 5,
              bgcolor: "#f0f2f5",
              "& fieldset": { border: "none" },
            },
          }}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, px: 1 }}>
          {search ? "Resultados de búsqueda" : "Seleccionados"}
        </Typography>

        {/* LISTA CON SCROLL */}
        <List
          sx={{
            width: "100%",
            flexGrow: 1,
            overflowY: "auto",
            bgcolor: "background.paper",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#bcc0c4",
              borderRadius: "10px",
            },
          }}
        >
          {displayList.length === 0 && (
            <Box sx={{ textAlign: "center", color: "text.secondary", mt: 5 }}>
              <Typography variant="body2">
                {search
                  ? "No se encontraron amigos"
                  : "Busca amigos para agregar a la lista"}
              </Typography>
            </Box>
          )}

          {displayList.map((friend) => {
            const isSelected = selected.some((f) => f.id === friend.id);
            return (
              <ListItem
                key={friend.id}
                component="div"
                onClick={() => toggleFriend(friend)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f2f2f2" },
                  bgcolor: isSelected
                    ? "rgba(24, 119, 242, 0.08)"
                    : "transparent",
                }}
              >
                <ListItemAvatar>
                  <Avatar src={friend.avatar}>
                    {friend.nombre?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  // Usamos el 'display' que ya viene construido o concatenamos con seguridad
                  primary={
                    friend.display ||
                    `${friend.nombre || ""} ${friend.apellido || ""}`.trim() ||
                    "Usuario desconocido"
                  }
                  secondary={isSelected ? "Seleccionado" : "Amigo"}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                />

                {/* Ícono dinámico según el tipo de privacidad */}
                {type === "friends_except" ? (
                  <IconButton edge="end">
                    <RemoveCircleOutline
                      sx={{ color: isSelected ? "#fa3e3e" : "#ced0d4" }}
                    />
                  </IconButton>
                ) : (
                  <IconButton edge="end">
                    <CheckCircle
                      sx={{ color: isSelected ? "#1877f2" : "#ced0d4" }}
                    />
                  </IconButton>
                )}
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      {/* ACCIONES */}
      <DialogActions sx={{ p: 2, borderTop: "1px solid #e4e6eb" }}>
        <Button
          onClick={onClose}
          sx={{ color: "#1877f2", fontWeight: 600, textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(selected)}
          sx={{
            bgcolor: "#1877f2",
            px: 4,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          Guardar cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FriendSelectionDialog;
